import type {
	Product,
	ProductItem
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import type {
	BasketItem,
	Basket
} from "@nwt/norce-pwa/dist/runtime/types/api/shopping/basket";
import type { FilteredProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import {
	createGtmItem,
	type CreateGtmItemContext,
	type GtmItem
} from "../types/composables/useGtmEventsTypes";
import type { CombinedItem } from "~/types/components/category";

const noopApi = () => ({
	productImpression: () => {},
	productClick: () => {},
	productDetail: () => {},
	addToCart: () => {},
	addItemsToCart: () => {},
	addPaymentInfo: () => {},
	addShippingInfo: () => {},
	purchase: () => {},
	removeFromCart: () => {},
	beginCheckout: () => {},
	viewCart: () => {},
	addToWishlist: () => {},
	shippingMethodAdded: () => {},
	pageView: () => {},
	login: () => {},
	signUp: () => {},
	userIdentify: () => {},
	userAnonymize: () => {},
	cartState: () => {},
	cartCleared: () => {}
});

export const useGtmEvents = () => {
	const getLayer = () => {
		if (import.meta.client) {
			return ((window as unknown as { dataLayer?: unknown[] }).dataLayer =
				(window as unknown as { dataLayer?: unknown[] }).dataLayer || []);
		}
		return [];
	};
	const gtm = getLayer();
	const norceStore = useNorceStore();
	const currencyCode = norceStore?.currentCurrency?.Code || "SEK";

	if (!gtm) return noopApi();

	const config = useRuntimeConfig();
	// [NF-460] Dotdigital's listing event rides on view_item_list; a no-op
	// unless the deployment enables Dotdigital and marketing consent is given.
	const dotdigital = useDotdigital();
	const cdnBaseUrl = (config.public.norce as { cdn?: string } | undefined)?.cdn;
	const itemContext: CreateGtmItemContext = {
		cdnBaseUrl,
		origin: import.meta.client ? window.location.origin : undefined
	};
	const mapItem = (
		item: Parameters<typeof createGtmItem>[0],
		category?: string | number
	) => createGtmItem(item, category, 1, itemContext);

	// GA4 drops a monetary value that arrives without a currency, and the
	// container's tags read both, so every item event sends the pair.
	const sumValue = (items: (GtmItem | null)[]): number =>
		Math.round(
			items.reduce(
				(total, item) => total + (item ? item.price * item.quantity : 0),
				0
			) * 100
		) / 100;

	// item_list_id is derived rather than taken from CategoryId: a search or
	// mixed list has no single category, and the slug stays stable per list name.
	const listId = (name?: string) =>
		name
			? name
					.toLowerCase()
					.trim()
					.replace(/\s+/g, "-")
					.replace(/[^a-z0-9åäö-]/g, "")
			: undefined;

	// Market as an ISO-3166 country code (e.g. "SE"), matching order.country.
	// Resolution order:
	//  1. checkout delivery-country override. It is recorded per locale, so
	//     another store's is not what this one reads and a locale switch needs
	//     no cleanup.
	//  2. the loaded application's default country (precise, once initApp ran).
	//  3. the region part of the locale iso (e.g. "sv-SE" -> "SE"), available
	//     synchronously from the SSR-rendered <html lang> so the very first
	//     page_view after a locale switch already reports the new market.
	const siteMarket = () =>
		norceStore?.countryCode ||
		norceStore?.applicationNorce?.Countries?.Default?.Code ||
		(import.meta.client
			? document.documentElement.lang?.split("-")[1]?.toUpperCase()
			: undefined) ||
		undefined;

	return {
		productImpression(
			products: Partial<FilteredProductItem>[] | CombinedItem[],
			categoryName?: string
		) {
			const productItems = products
				.map((item) => {
					if (
						"type" in item &&
						item.type === "product" &&
						"data" in item &&
						item.data
					) {
						return item.data as Partial<FilteredProductItem>;
					}
					return item as Partial<FilteredProductItem>;
				})
				.filter((item) => item != null);

			const items = productItems
				.map((product) => mapItem(product, categoryName))
				.filter((item) => item != null);

			gtm.push({
				event: "view_item_list",
				site_market: siteMarket(),
				ecommerce: {
					...(categoryName
						? {
								item_list_name: categoryName,
								item_list_id: listId(categoryName)
							}
						: {}),
					items
				}
			});
			dotdigital.productList(products);
		},

		// No list context: every caller (ProductCard, ProductVariants,
		// RelationVariants, VariantFamily, and the template forks of each) has
		// only the product. Threading a list name through would mean touching
		// every ProductCard fork, so item_list_name/id are left to
		// view_item_list rather than shipped here as dead parameters.
		productClick(product: Partial<FilteredProductItem> | ProductItem) {
			const item = mapItem(product);
			gtm.push({
				event: "select_item",
				site_market: siteMarket(),
				ecommerce: {
					currency: currencyCode,
					value: sumValue([item]),
					items: [item]
				}
			});
		},

		productDetail(product: Product) {
			const item = mapItem(product);
			gtm.push({
				event: "view_item",
				site_market: siteMarket(),
				ecommerce: {
					currency: currencyCode,
					value: sumValue([item]),
					items: [item]
				}
			});
		},

		beginCheckout(basket: Basket) {
			if (!basket?.Items?.length || !basket?.Summary) {
				console.warn("Basket items or summary are missing for purchase event");
				return;
			}

			gtm.push({
				event: "begin_checkout",
				site_market: siteMarket(),
				ecommerce: {
					...(basket.DiscountCode ? { coupon: basket.DiscountCode } : {}),
					value: basket.Summary.Total.AmountIncVat,
					currency: basket.CurrencyCode || currencyCode,
					items: basket.Items?.map((item) => mapItem(item))
				}
			});
		},

		addPaymentInfo(basket: Basket, paymentMethod?: string) {
			gtm.push({
				event: "add_payment_info",
				site_market: siteMarket(),
				ecommerce: {
					value: basket.Summary?.Total.AmountIncVat,
					currency: currencyCode,
					payment_type: paymentMethod || basket.PaymentMethodId?.toString(),
					items: basket.Items?.map((item) => mapItem(item))
				}
			});
		},

		addShippingInfo(basket: Basket, shippingMethod?: string) {
			gtm.push({
				event: "add_shipping_info",
				site_market: siteMarket(),
				ecommerce: {
					value: basket.Summary?.Total.AmountIncVat,
					currency: currencyCode,
					shipping_tier: shippingMethod || basket.DeliveryMethodId?.toString(),
					items: basket.Items?.map((item) => mapItem(item))
				}
			});
		},

		purchase(basket: Basket) {
			if (!basket.Items || !basket.Summary) {
				console.warn("Basket items or summary are missing for purchase event");
				return;
			}

			gtm.push({
				event: "purchase",
				site_market: siteMarket(),
				ecommerce: {
					transaction_id: basket.OrderReference,
					value: basket.Summary.Total.AmountIncVat,
					currency: basket.CurrencyCode || currencyCode,
					tax: basket.Summary.Total.Vat,
					shipping: basket.Summary.Freigt.AmountIncVat,
					// Omitted rather than sent empty, so GA4 records no coupon
					// instead of a blank one.
					...(basket.DiscountCode ? { coupon: basket.DiscountCode } : {}),
					items: basket.Items?.map((item) => mapItem(item))
				}
			});
		},

		addToCart(item: ProductItem | Product) {
			const mapped = mapItem(item);
			gtm.push({
				event: "add_to_cart",
				site_market: siteMarket(),
				ecommerce: {
					currency: currencyCode,
					value: sumValue([mapped]),
					items: [mapped]
				}
			});
		},

		/**
		 * One `add_to_cart` for several products added by a single action, with
		 * each product's real quantity — `addToCart` reports one product and
		 * `mapItem` assumes a quantity of one. Used by the multi pack, where a
		 * click adds N items in amounts that are >1 by design.
		 */
		addItemsToCart(
			entries: { product: ProductItem | Product; quantity: number }[]
		) {
			if (!entries.length) return;
			// createGtmItem returns null for an item with no Id/Name — dropped
			// rather than pushed as a null hole, the same guard productImpression
			// applies.
			const items = entries
				.map(({ product, quantity }) =>
					createGtmItem(product, undefined, quantity, itemContext)
				)
				.filter((item) => item !== null);
			gtm.push({
				event: "add_to_cart",
				site_market: siteMarket(),
				ecommerce: {
					currency: currencyCode,
					value: sumValue(items),
					items
				}
			});
		},

		removeFromCart(item: Partial<BasketItem>) {
			const mapped = mapItem(item);
			gtm.push({
				event: "remove_from_cart",
				site_market: siteMarket(),
				ecommerce: {
					currency: currencyCode,
					value: sumValue([mapped]),
					items: [mapped]
				}
			});
		},

		viewCart(items: BasketItem[], orderTotal: number) {
			gtm.push({
				event: "view_cart",
				site_market: siteMarket(),
				ecommerce: {
					currency: currencyCode,
					value: orderTotal,
					items: items.map((item) => mapItem(item))
				}
			});
		},

		addToWishlist(item: Product | Partial<FilteredProductItem> | ProductItem) {
			gtm.push({
				event: "add_to_wishlist",
				site_market: siteMarket(),
				ecommerce: {
					items: [mapItem(item)]
				}
			});
		},

		shippingMethodAdded(shippingMethod: object) {
			gtm.push({
				event: "shipping_method_added",
				site_market: siteMarket(),
				ecommerce: {
					price: shippingMethod?.Price || 0,
					name: shippingMethod?.Name || "",
					carrier: shippingMethod?.Carrier || ""
				}
			});
		},

		// SPA route-change pageview. No page_path/page_title: the GA4 tag
		// auto-collects page_location and page_title at send time, which is
		// correct after navigation and avoids sending a stale title.
		pageView() {
			gtm.push({ event: "page_view", site_market: siteMarket() });
		},

		// GA4 standard auth events. `method` is the spec parameter — the
		// container's login/sign_up tags still read a Jetshop-era `trackingId`
		// and need repointing to pick it up.
		login(method: string = "password") {
			gtm.push({ event: "login", site_market: siteMarket(), method });
		},

		signUp(method: string = "password") {
			gtm.push({ event: "sign_up", site_market: siteMarket(), method });
		},

		// User identify: pushed when an authenticated user becomes known.
		// GTM tags route this to the configured CDP (Dotdigital, etc.).
		userIdentify(email: string) {
			if (!email) return;
			gtm.push({
				event: "user_identify",
				site_market: siteMarket(),
				user: { email: email.trim().toLowerCase() }
			});
		},

		// Logout / session anonymize.
		userAnonymize() {
			gtm.push({ event: "user_anonymize", site_market: siteMarket() });
		},

		// Stateful snapshot of the current cart. Fires on every basket mutation
		// so CDPs that track abandoned carts (Dotdigital cartInsight, Klaviyo
		// Started Cart, etc.) get a full picture rather than incremental deltas.
		cartState(basket: Basket) {
			if (!basket?.Items?.length || !basket.Summary) return;
			const cartUrl = import.meta.client
				? `${window.location.origin}/checkout`
				: undefined;
			gtm.push({
				event: "cart_state",
				site_market: siteMarket(),
				ecommerce: {
					cart_id: basket.Id != null ? String(basket.Id) : undefined,
					currency: basket.CurrencyCode || currencyCode,
					subtotal: basket.Summary.Items?.Amount ?? 0,
					grand_total: basket.Summary.Total?.AmountIncVat ?? 0,
					tax_amount: basket.Summary.Total?.Vat,
					shipping: basket.Summary.Freigt?.AmountIncVat,
					discount_code: basket.DiscountCode || undefined,
					cart_url: cartUrl,
					items: basket.Items.map((item) => mapItem(item))
				}
			});
		},

		// Cart emptied — pair event to cart_state so CDPs can reset their
		// in-flight cart state.
		cartCleared() {
			gtm.push({
				event: "cart_cleared",
				site_market: siteMarket(),
				ecommerce: { currency: currencyCode, items: [] }
			});
		}
	};
};
